import { UnableToConnectException, NotFoundException, AuthenticationException, BadRequestException } from '../services/exceptions';
import { selectors as appSelectors, actions as appActions } from '../modules/appModule';

import config from '../config.out.json';

import * as advisers from './advisersApi'
import * as auth from './authApi'
import * as companiesHouse from './companiesHouseApi'
import * as entities from './entitiesApi'
import * as monitoring from './monitoringApi'
import * as favourites from './favouritesApi'
import * as lse from './lseApi'
import * as news from './newsApi'
import * as checkboxes from './checkboxesApi'
import * as synopsis from './synopsisApi'

export { advisers, auth, checkboxes, companiesHouse, entities, favourites, lse, monitoring, news, synopsis }

/* Dependency injection
----------------------------------------------------------------------------------------------------------------------*/
let store; // cant import store directly because it would be circular
export function setStore(_store) {
  store = _store;
}

/* Private functions
----------------------------------------------------------------------------------------------------------------------*/
function kickOutUser() {
  store.dispatch(appActions.logout());
}

function getAuthHeader() {
  return 'Bearer ' + appSelectors.getAuthToken(store.getState().app);
}

async function request(path, settings) {
  let response;
  try {
    response = await fetch(`//${config.api}${path}`, settings);
  } catch (e) {
    console.log(e);
    throw new UnableToConnectException('Unable to connect to server.');
  }

  if (response.status === 401) {
    kickOutUser();
    throw new AuthenticationException((await response.json()).message);
  }
  if (response.status === 404) throw new NotFoundException((await response.json()).message);
  if (response.status === 500) throw new UnableToConnectException((await response.json()).message);

  return response;
}

async function get(path) {
  return await request(path, {
    headers: {
      Authorization: getAuthHeader()
    }
  });
}

async function del(path) {
  return await request(path, {
    method: 'DELETE',
    headers: {
      Authorization: getAuthHeader()
    }
  });
}

async function post(path, body) {
  return await request(path, {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    body: JSON.stringify(body)
  });
}

async function patch(path, body) {
  return await request(path, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    body: JSON.stringify(body)
  });
}

/* Public functions
----------------------------------------------------------------------------------------------------------------------*/
export async function ping() {
  const response = await request('/');
  if (response.status === 200) {
    return;
  }
  throw new UnableToConnectException('Api is unaccessible.');
}

export async function register(user) {
  const response = await post('/users', {
    name: user.name,
    email: user.email,
    password: user.password,
    dataUseAgreement: user.hasOwnProperty('dataUseAgreement') && user.dataUseAgreement ? 1 : 0,
    marketingUseAgreement: user.hasOwnProperty('marketingUseAgreement') && user.marketingUseAgreement ? 1 : 0,
    origin: user.origin || 'dummyOrigin'
  });
  if (response.status === 200) {
    return await response.json();
  }

  throw new BadRequestException((await response.json()).message);
}

export async function updateUser(user) {
  const response = await patch('/users/me', {
    name: user.name,
    email: user.email,
    marketingUseAgreement: user.hasOwnProperty('marketingUseAgreement') && user.marketingUseAgreement ? 1 : 0
  });
  if (response.status === 200) {
    return await response.json();
  }

  throw new BadRequestException((await response.json()).message);
}

export async function getMe() {
  const response = await get('/users/me');
  if (response.status === 200) {
    return await response.json();
  }
  if (response.status === 401) {
    throw new AuthenticationException('User is not authenticated');
  }
  throw new BadRequestException('Unknown error on getMe');
}

export async function deleteMe() {
  const response = await del('/users/me');
  if (response.status === 200) {
    return await response.json();
  }
  if (response.status === 401) {
    throw new AuthenticationException('User is not authenticated');
  }
  throw new BadRequestException('Unknown error on deleteMe');
}
